import { BaseModule } from './baseModule';
import type { AxiosInstance } from 'axios';
import type { ErrorHandler } from './errorHandler';
import type { MessageResponse, RequestConfig } from './types';

/**
 * Testimonial interface
 */
export interface Testimonial {
  id: string;
  user_id: string;
  user_name: string;
  user_email: string;
  user_avatar?: string;
  content: string;
  rating?: number;
  position?: string;
  company?: string;
  featured: boolean;
  approved: boolean;
  created_at?: string;
  updated_at?: string;
}

/**
 * Create testimonial request
 */
export interface CreateTestimonialRequest {
  content: string;
  rating?: number;
  position?: string;
  company?: string;
}

/**
 * Update testimonial request
 */
export interface UpdateTestimonialRequest {
  content?: string;
  rating?: number;
  position?: string;
  company?: string;
}

/**
 * TestimonialsModule - Handles all testimonial-related API operations
 */
export class TestimonialsModule extends BaseModule {
  constructor(httpClient: AxiosInstance, errorHandler: ErrorHandler) {
    super(httpClient, errorHandler);
  }

  // ============================================================================
  // Admin Operations
  // ============================================================================

  /**
   * Get all testimonials (admin)
   * GET /api/admin/testimonials
   */
  async getAllTestimonials(config?: RequestConfig): Promise<Testimonial[]> {
    return this.get<Testimonial[]>('/api/admin/testimonials', config);
  }

  /**
   * Get testimonials for a specific candidate (admin)
   * GET /api/admin/candidates/:candidateId/testimonials
   */
  async getCandidateTestimonials(
    candidateId: string,
    config?: RequestConfig
  ): Promise<Testimonial[]> {
    return this.get<Testimonial[]>(
      `/api/admin/candidates/${candidateId}/testimonials`,
      config
    );
  }

  /**
   * Approve or unapprove a testimonial (admin)
   * POST /api/admin/testimonials/:id/approve
   */
  async approveTestimonial(
    id: string,
    approved: boolean,
    config?: RequestConfig
  ): Promise<MessageResponse> {
    return this.post<MessageResponse>(
      `/api/admin/testimonials/${id}/approve`,
      { approved },
      config
    );
  }

  /**
   * Feature or unfeature a testimonial (admin)
   * POST /api/admin/testimonials/:id/feature
   */
  async featureTestimonial(
    id: string,
    featured: boolean,
    config?: RequestConfig
  ): Promise<MessageResponse> {
    return this.post<MessageResponse>(
      `/api/admin/testimonials/${id}/feature`,
      { featured },
      config
    );
  }

  // ============================================================================
  // User Operations
  // ============================================================================

  /**
   * Get user's own testimonials
   * GET /api/testimonials/my
   */
  async getUserTestimonials(config?: RequestConfig): Promise<Testimonial[]> {
    return this.get<Testimonial[]>('/api/testimonials/my', config);
  }

  /**
   * Create a new testimonial
   * POST /api/testimonials
   */
  async createTestimonial(
    data: CreateTestimonialRequest,
    config?: RequestConfig
  ): Promise<Testimonial> {
    return this.post<Testimonial>('/api/testimonials', data, config);
  }

  /**
   * Update a testimonial
   * PUT /api/testimonials/:id
   */
  async updateTestimonial(
    id: string,
    data: UpdateTestimonialRequest,
    config?: RequestConfig
  ): Promise<Testimonial> {
    return this.put<Testimonial>(`/api/testimonials/${id}`, data, config);
  }

  /**
   * Delete a testimonial
   * DELETE /api/testimonials/:id
   */
  async deleteTestimonial(
    id: string,
    config?: RequestConfig
  ): Promise<MessageResponse> {
    return this.delete<MessageResponse>(`/api/testimonials/${id}`, config);
  }

  // ============================================================================
  // Public Operations
  // ============================================================================

  /**
   * Get approved testimonials (public)
   * GET /api/testimonials
   */
  async getApprovedTestimonials(config?: RequestConfig): Promise<Testimonial[]> {
    return this.get<Testimonial[]>('/api/testimonials', config);
  }

  /**
   * Get featured testimonials (public)
   * GET /api/testimonials
   */
  async getFeaturedTestimonials(config?: RequestConfig): Promise<Testimonial[]> {
    return this.get<Testimonial[]>('/api/testimonials', config);
  }
}
